import { useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Star } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, FieldWrapper } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';

/**
 * Structured editor for the `services.pricing` JSONB payload.
 *
 * Persistence: the public /services card renders the JSON shape unchanged
 * (see ServicePricing in lib/cms-types.ts). This editor builds and maintains
 * that object in memory; ServiceEditForm hands it back to the parent as a
 * plain object, which is then written verbatim to the jsonb column.
 */

interface Tier {
  name: string;
  bestFor: string;
  length: string;
  includes: string[];
  priceGHS: number;
  priceUSD: number;
  popular: boolean;
}

interface MonthlyPackage {
  name: string;
  videosPerMonth: string;
  bestFor: string;
  priceGHS: number;
  priceUSD: number;
  popular: boolean;
}

interface Addon {
  name: string;
  price: string;
}

interface Rules {
  footage?: string;
  revisions?: string;
  payment?: string;
  delivery?: string;
}

interface Bundle {
  headline: string;
  body: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export interface PricingValue {
  headline?: string;
  lede?: string;
  categories?: string[];
  tiers: Tier[];
  monthly: MonthlyPackage[];
  addons: Addon[];
  rules: Rules;
  bundle?: Bundle;
}

interface Props {
  value: PricingValue;
  onChange: (next: PricingValue) => void;
}

/* ------------------------------------------------------------------ helpers */
function num(v: string, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function ChipInput({
  label,
  items,
  onChange,
  placeholder,
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  return (
    <FieldWrapper label={label}>
      <ChipArray items={items} onChange={onChange} placeholder={placeholder} />
    </FieldWrapper>
  );
}

function ChipArray({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  function add() {
    const v = draft.trim();
    if (!v) return;
    if (!items.includes(v)) onChange([...items, v]);
    setDraft('');
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, idx) => (
          <span
            key={`${it}-${idx}`}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg"
          >
            {it}
            <button
              type="button"
              aria-label={`Remove ${it}`}
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-fg-subtle hover:text-danger"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && (
          <span className="text-xs text-fg-subtle">None yet.</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder={placeholder ?? 'Add an item'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="secondary" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={add}>
          Add
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ intro */
function IntroSection({
  value,
  onChange,
}: {
  value: Pick<PricingValue, 'headline' | 'lede' | 'categories'>;
  onChange: (next: Pick<PricingValue, 'headline' | 'lede' | 'categories'>) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Header</p>
      <FieldWrapper label="Headline" hint="Optional eyebrow / sub-title for the pricing block.">
        <Input
          value={value.headline ?? ''}
          onChange={(e) => onChange({ ...value, headline: e.target.value || undefined })}
          placeholder="Short-Form Video Content (Up to 4 Minutes)"
        />
      </FieldWrapper>
      <FieldWrapper label="Lede" hint="One-paragraph pitch shown above the tier cards.">
        <Textarea
          rows={3}
          value={value.lede ?? ''}
          onChange={(e) => onChange({ ...value, lede: e.target.value || undefined })}
          placeholder="Turn your idea or raw footage into scroll-stopping short videos…"
        />
      </FieldWrapper>
      <ChipInput
        label="Categories"
        items={value.categories ?? []}
        onChange={(next) => onChange({ ...value, categories: next })}
        placeholder="e.g. Reels"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ tiers */
const BLANK_TIER: Tier = {
  name: '',
  bestFor: '',
  length: '',
  includes: [],
  priceGHS: 0,
  priceUSD: 0,
  popular: false,
};

function TiersSection({
  value,
  onChange,
}: {
  value: Tier[];
  onChange: (next: Tier[]) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Single-video tiers</p>
          <p className="mt-0.5 text-xs text-fg-subtle">One tier can be marked “Most popular” — it gets the gold ring.</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => onChange([...value, { ...BLANK_TIER }])}
        >
          Add tier
        </Button>
      </div>
      {value.length === 0 && (
        <p className="text-xs text-fg-subtle">No tiers yet — add one above.</p>
      )}
      <div className="space-y-4">
        {value.map((tier, idx) => (
          <TierRow
            key={idx}
            tier={tier}
            onChange={(next) => onChange(value.map((t, i) => (i === idx ? next : t)))}
            onRemove={() => onChange(value.filter((_, i) => i !== idx))}
            onMoveUp={idx > 0 ? () => onChange(reorder(value, idx, idx - 1)) : undefined}
            onMoveDown={idx < value.length - 1 ? () => onChange(reorder(value, idx, idx + 1)) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function TierRow({
  tier,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  tier: Tier;
  onChange: (next: Tier) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-elevated/30 p-4">
      <div className="flex items-start justify-between gap-2">
        <Input
          aria-label="Tier name"
          placeholder="Tier name (e.g. Standard)"
          value={tier.name}
          onChange={(e) => onChange({ ...tier, name: e.target.value })}
          className="font-medium"
        />
        <div className="flex items-center gap-1">
          {onMoveUp && (
            <Button type="button" variant="ghost" size="icon" aria-label="Move up" onClick={onMoveUp}>
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
          )}
          {onMoveDown && (
            <Button type="button" variant="ghost" size="icon" aria-label="Move down" onClick={onMoveDown}>
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button type="button" variant="ghost" size="icon" aria-label="Remove tier" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <FieldWrapper label="Best for">
          <Input
            value={tier.bestFor}
            onChange={(e) => onChange({ ...tier, bestFor: e.target.value })}
            placeholder="Businesses, product showcases…"
          />
        </FieldWrapper>
        <FieldWrapper label="Length">
          <Input
            value={tier.length}
            onChange={(e) => onChange({ ...tier, length: e.target.value })}
            placeholder="1 - 2 mins"
          />
        </FieldWrapper>
        <FieldWrapper label="Price GH₵">
          <Input
            type="number"
            min={0}
            step={50}
            value={String(tier.priceGHS)}
            onChange={(e) => onChange({ ...tier, priceGHS: num(e.target.value) })}
          />
        </FieldWrapper>
        <FieldWrapper label="Price USD">
          <Input
            type="number"
            min={0}
            step={5}
            value={String(tier.priceUSD)}
            onChange={(e) => onChange({ ...tier, priceUSD: num(e.target.value) })}
          />
        </FieldWrapper>
      </div>

      <div className="mt-3">
        <ChipInput
          label="Includes"
          items={tier.includes}
          onChange={(next) => onChange({ ...tier, includes: next })}
          placeholder="e.g. Scriptwriting"
        />
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs text-fg-muted">
        <Switch
          checked={tier.popular}
          onCheckedChange={(c) => onChange({ ...tier, popular: !!c })}
          aria-label="Mark as most popular"
        />
        <Star className="h-3 w-3 text-gold-500" />
        Mark as “Most popular” (only one tier at a time)
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ monthly */
const BLANK_MONTHLY: MonthlyPackage = {
  name: '',
  videosPerMonth: '',
  bestFor: '',
  priceGHS: 0,
  priceUSD: 0,
  popular: false,
};

function MonthlySection({
  value,
  onChange,
}: {
  value: MonthlyPackage[];
  onChange: (next: MonthlyPackage[]) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Monthly packages</p>
          <p className="mt-0.5 text-xs text-fg-subtle">Retainer-style bundles shown below the tiers.</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => onChange([...value, { ...BLANK_MONTHLY }])}
        >
          Add package
        </Button>
      </div>
      {value.length === 0 && (
        <p className="text-xs text-fg-subtle">No packages yet.</p>
      )}
      <div className="space-y-4">
        {value.map((pkg, idx) => (
          <MonthlyRow
            key={idx}
            pkg={pkg}
            onChange={(next) => onChange(value.map((p, i) => (i === idx ? next : p)))}
            onRemove={() => onChange(value.filter((_, i) => i !== idx))}
            onMoveUp={idx > 0 ? () => onChange(reorder(value, idx, idx - 1)) : undefined}
            onMoveDown={idx < value.length - 1 ? () => onChange(reorder(value, idx, idx + 1)) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function MonthlyRow({
  pkg,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  pkg: MonthlyPackage;
  onChange: (next: MonthlyPackage) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-elevated/30 p-4">
      <div className="flex items-start justify-between gap-2">
        <Input
          aria-label="Package name"
          placeholder="Package name (e.g. Growth)"
          value={pkg.name}
          onChange={(e) => onChange({ ...pkg, name: e.target.value })}
          className="font-medium"
        />
        <div className="flex items-center gap-1">
          {onMoveUp && (
            <Button type="button" variant="ghost" size="icon" aria-label="Move up" onClick={onMoveUp}>
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
          )}
          {onMoveDown && (
            <Button type="button" variant="ghost" size="icon" aria-label="Move down" onClick={onMoveDown}>
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button type="button" variant="ghost" size="icon" aria-label="Remove package" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <FieldWrapper label="Videos per month" hint="e.g. “10 videos (up to 2 min)”">
          <Input
            value={pkg.videosPerMonth}
            onChange={(e) => onChange({ ...pkg, videosPerMonth: e.target.value })}
            placeholder="10 videos (up to 2 min)"
          />
        </FieldWrapper>
        <FieldWrapper label="Best for">
          <Input
            value={pkg.bestFor}
            onChange={(e) => onChange({ ...pkg, bestFor: e.target.value })}
            placeholder="Active brands, startups…"
          />
        </FieldWrapper>
        <FieldWrapper label="Price GH₵">
          <Input
            type="number"
            min={0}
            step={50}
            value={String(pkg.priceGHS)}
            onChange={(e) => onChange({ ...pkg, priceGHS: num(e.target.value) })}
          />
        </FieldWrapper>
        <FieldWrapper label="Price USD">
          <Input
            type="number"
            min={0}
            step={5}
            value={String(pkg.priceUSD)}
            onChange={(e) => onChange({ ...pkg, priceUSD: num(e.target.value) })}
          />
        </FieldWrapper>
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs text-fg-muted">
        <Switch
          checked={pkg.popular}
          onCheckedChange={(c) => onChange({ ...pkg, popular: !!c })}
          aria-label="Mark as most popular"
        />
        <Star className="h-3 w-3 text-gold-500" />
        Mark as “Most popular”
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ addons */
const BLANK_ADDON: Addon = { name: '', price: '' };

function AddonsSection({
  value,
  onChange,
}: {
  value: Addon[];
  onChange: (next: Addon[]) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Add-ons</p>
          <p className="mt-0.5 text-xs text-fg-subtle">Extras like rush delivery or extra revisions.</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => onChange([...value, { ...BLANK_ADDON }])}
        >
          Add add-on
        </Button>
      </div>
      {value.length === 0 && <p className="text-xs text-fg-subtle">No add-ons.</p>}
      <div className="space-y-3">
        {value.map((addon, idx) => (
          <div key={idx} className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Add-on name"
              placeholder="Name"
              value={addon.name}
              onChange={(e) => onChange(value.map((a, i) => (i === idx ? { ...a, name: e.target.value } : a)))}
              className="flex-1 min-w-[200px]"
            />
            <Input
              aria-label="Add-on price"
              placeholder="Price (e.g. GH₵ 1,200 – 2,500 / day)"
              value={addon.price}
              onChange={(e) => onChange(value.map((a, i) => (i === idx ? { ...a, price: e.target.value } : a)))}
              className="flex-1 min-w-[240px]"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove add-on"
              onClick={() => onChange(value.filter((_, i) => i !== idx))}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ rules */
function RulesSection({
  value,
  onChange,
}: {
  value: Rules;
  onChange: (next: Rules) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Terms & logistics</p>
        <p className="mt-0.5 text-xs text-fg-subtle">Short blurbs shown in the “Terms” card.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FieldWrapper label="Footage">
          <Textarea
            rows={2}
            value={value.footage ?? ''}
            onChange={(e) => onChange({ ...value, footage: e.target.value || undefined })}
          />
        </FieldWrapper>
        <FieldWrapper label="Revisions">
          <Textarea
            rows={2}
            value={value.revisions ?? ''}
            onChange={(e) => onChange({ ...value, revisions: e.target.value || undefined })}
          />
        </FieldWrapper>
        <FieldWrapper label="Payment">
          <Textarea
            rows={2}
            value={value.payment ?? ''}
            onChange={(e) => onChange({ ...value, payment: e.target.value || undefined })}
          />
        </FieldWrapper>
        <FieldWrapper label="Delivery">
          <Textarea
            rows={2}
            value={value.delivery ?? ''}
            onChange={(e) => onChange({ ...value, delivery: e.target.value || undefined })}
          />
        </FieldWrapper>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ bundle */
function BundleSection({
  value,
  onChange,
}: {
  value: Bundle | undefined;
  onChange: (next: Bundle | undefined) => void;
}) {
  const enabled = !!value;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Bundle callout</p>
          <p className="mt-0.5 text-xs text-fg-subtle">Highlighted card shown below the rest.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-fg-muted">
          <Switch
            checked={enabled}
            onCheckedChange={(c) => onChange(c ? { headline: '', body: '', ctaLabel: 'Learn more', ctaHref: '' } : undefined)}
            aria-label="Toggle bundle callout"
          />
          {enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>
      {enabled && value && (
        <div className="space-y-3 rounded-lg border border-border bg-bg-elevated/30 p-4">
          <FieldWrapper label="Headline">
            <Input
              value={value.headline}
              onChange={(e) => onChange({ ...value, headline: e.target.value })}
              placeholder="Need 10+ videos a month?"
            />
          </FieldWrapper>
          <FieldWrapper label="Body">
            <Textarea
              rows={3}
              value={value.body}
              onChange={(e) => onChange({ ...value, body: e.target.value })}
              placeholder="Get our Content + Website Bundle…"
            />
          </FieldWrapper>
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldWrapper label="CTA label">
              <Input
                value={value.ctaLabel ?? ''}
                onChange={(e) => onChange({ ...value, ctaLabel: e.target.value || undefined })}
                placeholder="Explore the bundle"
              />
            </FieldWrapper>
            <FieldWrapper label="CTA href">
              <Input
                value={value.ctaHref ?? ''}
                onChange={(e) => onChange({ ...value, ctaHref: e.target.value || undefined })}
                placeholder="/contact?topic=get-videos"
              />
            </FieldWrapper>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ root */
function reorder<T>(arr: T[], from: number, to: number): T[] {
  const copy = arr.slice();
  const [moved] = copy.splice(from, 1);
  if (moved === undefined) return arr;
  copy.splice(to, 0, moved);
  return copy;
}

export function PricingEditor({ value, onChange }: Props) {
  return (
    <div className="space-y-6">
      <details className="rounded-md border border-border bg-bg-elevated/30 p-4" open>
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Intro & categories
        </summary>
        <div className="mt-4">
          <IntroSection
            value={{ headline: value.headline, lede: value.lede, categories: value.categories }}
            onChange={(next) => onChange({ ...value, ...next })}
          />
        </div>
      </details>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4" open>
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Single-video tiers ({value.tiers.length})
        </summary>
        <div className="mt-4">
          <TiersSection value={value.tiers} onChange={(next) => onChange({ ...value, tiers: next })} />
        </div>
      </details>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Monthly packages ({value.monthly.length})
        </summary>
        <div className="mt-4">
          <MonthlySection value={value.monthly} onChange={(next) => onChange({ ...value, monthly: next })} />
        </div>
      </details>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Add-ons ({value.addons.length})
        </summary>
        <div className="mt-4">
          <AddonsSection value={value.addons} onChange={(next) => onChange({ ...value, addons: next })} />
        </div>
      </details>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Terms & logistics
        </summary>
        <div className="mt-4">
          <RulesSection value={value.rules} onChange={(next) => onChange({ ...value, rules: next })} />
        </div>
      </details>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Bundle callout {value.bundle ? '(on)' : '(off)'}
        </summary>
        <div className="mt-4">
          <BundleSection value={value.bundle} onChange={(next) => onChange({ ...value, bundle: next })} />
        </div>
      </details>
    </div>
  );
}

/**
 * Convenience defaults so admins adding a new tiered service start with a
 * blank-but-valid payload (tiers/monthly/addons/rules are required arrays/objects
 * per ServicePricing). The VideoPricingBlock renders nothing if arrays are empty.
 */
export const EMPTY_PRICING: PricingValue = {
  tiers: [],
  monthly: [],
  addons: [],
  rules: {},
};